import {
    UploadOperationBase,
    UploadOperationConfig,
    UploadOperationEvents,
    UploadOperationResult, UploadOperationStrings
} from "./operations/uploadOperationBase";
import {ObservableArray, PureComputed, Subscribable} from "knockout";
import {FileUploadStatus} from "./models/fileToUpload";
import {FilesHelpers} from "../../../helpers/files";
import {injectable} from "inversify";
import autobind from "../../../decorators/autobind";
import {QualzyVideoRecorderConfig} from "../recorders/qualzyVideoRecorder/component";
import {QualzyScreenRecorderConfig} from "../recorders/qualzyScreenRecorder/component";
import {QualzyScreenVideoRecorderConfig} from "../recorders/qualzyScreenVideoRecorder/component";
import {QualzyAudioRecorderConfig} from "../recorders/qualzyAudioRecorder/component";
import {ILogsContainer, LogsContainer} from "../../../logsContainer";
import { UserLogsService } from "../../../services/userLogs";

/**
 * An uploader can upload files in multiple ways and so, this enum defines those modes
 */
enum QualzyUploaderModes {
    /**
     * Use file picker to pick file(s) and pass it/them to the uploader for uploading
     */
    FilePicker,

    /**
     * Use the video recorder component to record video through the webcam and pass it to the uploader for uploading
     */
    VideoRecorder,

    /**
     * Use the audio recorder component to record audio through the system's microphone and pass it to the uploader for uploading
     */
    AudioRecorder,

    /**
     * Use the screen recorder component to record the screen and pass it to the uploader for uploading
     */
    ScreenRecorder,

    /**
     * Use the screen and video recorder component to record the screen and video at the same time and pass the recording to the uploader for uploading
     */
    ScreenAndVideoRecorder
}

/**
 * This is the base of every uploader
 * An uploader is basically a wrapper around multiple upload operations or upload files.
 * Its job is to perform validations and create upload operations 
 * It is initialised with 3 generic parameters:
 * 1. Result R - The type of result which may differ from uploader to uploader. For example - S3Uploader may return additional data in the result class, while EndpointUploader may return
 * just the url of the uploaded file.
 * 2. Strings S - The static strings class for localised labels/messages - It has some base string resource strings + upload operation specific strings
 * 3. Config C - The type of configuration which may differ from uploader to uploader. For example - S3Uploader may have different configuration parameters (like bucket, region etc.),
 * while EndpointUploader may have completely different configuration parameters.
 * 4. Upload Operation U - The type of uploader which must be built with <R, S, E, C> as its generic arguments. This is the actual upload operation per file.
 */
@injectable()
export default abstract class QualzyUploaderBase<
    R extends UploadOperationResult, 
    S extends UploadOperationStrings,
    E extends UploadOperationEvents<R>, 
    C extends UploadOperationConfig<R, S, E>,
    U extends UploadOperationBase<R, S, E, C>> {

    /**
     * The current mode the uploader is in. This is mainly used to switch UI.
     * @private
     */
    private readonly _uploaderMode = ko.observable(QualzyUploaderModes.FilePicker);

    /**
     * True if the uploader is set to "pick files" mode
     * @private
     */
    private isInFilePickerMode = ko.pureComputed(() => this._uploaderMode() == QualzyUploaderModes.FilePicker);

    /**
     * True if the uploader is set to "video recording" mode
     * @private
     */
    private isInVideoRecorderMode = ko.pureComputed(() => this._uploaderMode() == QualzyUploaderModes.VideoRecorder);

    /**
     * True if the uploader is set to "Audio recording" mode
     * @private
     */
    private isInAudioRecorderMode = ko.pureComputed(() => this._uploaderMode() == QualzyUploaderModes.AudioRecorder);

    /**
     * True if the uploader is set to "screen recording" mode
     * @private
     */
    private isInScreenRecorderMode = ko.pureComputed(() => this._uploaderMode() == QualzyUploaderModes.ScreenRecorder);

    /**
     * True if the uploader is set to "screen and video recording" mode
     * @private
     */
    private isInScreenAndVideoRecorderMode = ko.pureComputed(() => this._uploaderMode() == QualzyUploaderModes.ScreenAndVideoRecorder);

    /**
     * True if the uploader is in recording mode (video, audio, screen or screen and video recording mode)
     */
    private isInRecordingMode = ko.pureComputed(() => this.isInVideoRecorderMode() || this.isInAudioRecorderMode() || this.isInScreenRecorderMode() || this.isInScreenAndVideoRecorderMode());

    /**
     * The configuration for the uploader
     * @protected
     */
    protected readonly _config: C;
    
    /**
     * The user logs service to send logs to the server
     * @protected
     */
    protected readonly _userLogsService: UserLogsService;
    
    /**
     * The string representing the maximum allowed size (in MBs)
     */
    public readonly maxSizeInMbAllowedStr: string;

    /**
     * The string representing the allowed file extensions
     */
    public readonly extensionsAllowedStr: string;
    
    /**
     * List of file upload operations
     * @private This is private because this is changed internally
     */
    private readonly _uploads: ObservableArray<U> = ko.observableArray<U>([]);

    /**
     * Returns the list of file upload operations
     */
    uploads = ko.pureComputed(() => this._uploads());

    /**
     * Return true if the uploader has any upload operations in the list
     */
    hasUploads = ko.pureComputed(() => this._uploads().length > 0);

    /**
     * Return true if the uploader is allow to handle multiple files
     */
    allowMultipleFiles: boolean;

    /**
     * Returns true if the uploader can upload files
     * This is true if the uploader has any pending upload operations queued which are either in the 'Initialized' (Ready to upload) or 'Cancelled' (Ready to retry) state
     * 
     */
    canUpload: PureComputed<boolean> = ko.pureComputed(() => this._uploads().some(f => [FileUploadStatus.Initialized, FileUploadStatus.Cancelled].includes(f.status())));

    isScreenRecord = ko.pureComputed(() => this.screenRecordingIsAllowed || this.screenAndVideoRecordingIsAllowed);

    /**
     * True if video recording from webcam is allowed; False otherwise
     */
    public readonly videoRecordingIsAllowed: boolean;

    /**
     * True if audio recording is allowed; False otherwise
     */
    public readonly audioRecordingIsAllowed: boolean;

    /**
     * True if screen recording is allowed; False otherwise
     */
    public readonly screenRecordingIsAllowed: boolean;

    /**
     * True if combined screen and video recording is allowed; False otherwise
     */
    public readonly screenAndVideoRecordingIsAllowed: boolean;

    /**
     * True if the uploader can record (video, audio, screen or screen and video); False otherwise
     */
    canRecord: PureComputed<boolean>;

    /*
     * True if the uploader has multiple actions (like video recorder, audio recorder, screen recorder etc.); False otherwise
    */
    public readonly hasSingleAction: PureComputed<boolean>;

    /**
     * The action to be performed when only one action available and the user clicks on the record button
     */
    public readonly onClickRecordAction?: Action = undefined;

    /**
     * True if video recording is allowed OR screen recording is allowed
     */
    public readonly allowCameraCapture: boolean;

    /**
     * True if pick file is turned on for activity settings
     */
    public readonly pickFilesAllowed: boolean;

    /**
     * Dynamic title for record/upload (as upload can be turned off)
     */
    dialogTitleRecordUpload: PureComputed<string>;

    /**
     * Dynamic sub-title for record/upload (as upload can be turned off)
     */
    dialogSubTitleRecordUpload: PureComputed<string>;

    /**
     * True if microphone turned on for activity settings
     */
    public readonly microphoneAllowed: boolean;

    /**
     * The configuration object for video recorder
     */
    public readonly videoRecorderConfig: QualzyVideoRecorderConfig | null = null;

    /**
     * The configuration object for audio recorder
     */
    public readonly audioRecorderConfig: QualzyAudioRecorderConfig | null = null;
    
    /**
     * The configuration object for screen recorder
     */
    public readonly screenRecorderConfig: QualzyScreenRecorderConfig | null = null;

    /**
     * The configuration object for the combined screen and video recorder
     */
    public readonly screenAndVideoRecorderConfig: QualzyScreenVideoRecorderConfig | null = null;

    /**
     * True if browser detects mobile device
     */
    isMobile: Subscribable<boolean>;

    /**
     * Any labels/static texts associated with the component
     * This is useful when we want to localize labels
     */
    public readonly StaticStrings: UploadOperationStrings
    
    /**
     * It is overridden in the subclasses, and it basically creates the right type of upload operation
     * This is quite important since it is used to initialise the operation after a file is picked by the picker
     * @param file The file picked by the picker
     * @param logs The logs container
     */
    protected abstract createUpload(file: File, logs: ILogsContainer): U;

    /**
     * Initialises the uploader with the right config
     * @param config The config for the uploader
     * @param userLogsService The user logs service to send logs to the server
     */
    constructor(config: C, userLogsService: UserLogsService) {
        this._config = config;
        this._userLogsService = userLogsService;

        this.StaticStrings = config.StaticStrings;

        this.pickFilesAllowed = this._config.AllowPickFiles;

        this.dialogTitleRecordUpload = ko.pureComputed(() => {
            if (this.isMobile()) {
                return this.isScreenRecord() ? this.StaticStrings.DialogTitleUploadRecording : this.StaticStrings.DialogTitleUploadOnly;
            }
            else if (this.pickFilesAllowed) {
                return this.isScreenRecord() ? this.StaticStrings.DialogTitleRecordUploadScreen : this.StaticStrings.DialogTitleRecordUpload;
            }
            else {
                return this.isScreenRecord() ? this.StaticStrings.DialogTitleRecordScreenOnly : this.StaticStrings.DialogTitleRecordOnly;
            }
        });

        this.dialogSubTitleRecordUpload = ko.pureComputed(() => {
            if (this.isMobile()) {
                return this.isScreenRecord() ? this.StaticStrings.DialogSubTitleUploadRecording : this.StaticStrings.DialogSubTitleUploadOnly;
            }
            else if (this.pickFilesAllowed) {
                return this.isScreenRecord() ? this.StaticStrings.DialogSubTitleRecordScreen : this.StaticStrings.DialogSubTitleRecordUpload;
            }
            else {
                return this.isScreenRecord() ? this.StaticStrings.DialogSubTitleRecordScreen : this.StaticStrings.DialogSubTitleRecordOnly;
            }
        });

        this.microphoneAllowed = this._config.AllowMicrophone;

        this.videoRecordingIsAllowed = this._config.AllowVideoRecording;
        this.audioRecordingIsAllowed = this._config.AllowAudioRecording;
        this.screenRecordingIsAllowed = this._config.AllowScreenRecording;
        this.screenAndVideoRecordingIsAllowed = this._config.AllowScreenAndVideoRecording

        this.canRecord = ko.pureComputed(() => this.videoRecordingIsAllowed || this.audioRecordingIsAllowed || this.screenRecordingIsAllowed || this.screenAndVideoRecordingIsAllowed);

        this.allowCameraCapture = this.videoRecordingIsAllowed || this.screenAndVideoRecordingIsAllowed;
        
        this.hasSingleAction = ko.pureComputed(() => {
            let numActions = (+this.videoRecordingIsAllowed) +
                (+this.audioRecordingIsAllowed) +
                (+this.screenRecordingIsAllowed) +
                (+this.screenAndVideoRecordingIsAllowed);
            
            return numActions == 1;
        });

        if (this.hasSingleAction()) {
            if (this.videoRecordingIsAllowed)
                this.onClickRecordAction = this.recordVideo;
            if (this.audioRecordingIsAllowed)
                this.onClickRecordAction = this.recordAudio;
            if (this.screenRecordingIsAllowed)
                this.onClickRecordAction = this.recordScreen;
            if (this.screenAndVideoRecordingIsAllowed)
                this.onClickRecordAction = this.recordScreenAndVideo;
        }

        this.allowMultipleFiles = this._config.AllowMultipleFiles;
        this.maxSizeInMbAllowedStr = this._config.AllowedMaxSizeInBytes == null ? "" : FilesHelpers.getSizeStr('MB', this._config.AllowedMaxSizeInBytes, 2);
        this.extensionsAllowedStr = this._config.getAllowedFileExtensionsStr();

        this.isMobile = ko.pureComputed(() => system.isMobileDevice());

        if (this._config.AllowVideoRecording) {
            this.videoRecorderConfig = {
                StaticStrings: {
                    PermissionNotGranted: this._config.StaticStrings.PermissionNotGranted,
                    StartRecording: this._config.StaticStrings.StartRecording,
                    StopRecording: this._config.StaticStrings.StopRecording
                },
                Events: {
                    OnRecordingStopped: (data, mimeTypeData, logs) => {
                        //When the video recording is done, create a file of blob and add it to the list of upload operations
                        const upload = this.createUpload(
                            new File(data, this._config.StaticStrings.NewRecordedVideoName.replace('{SUFFIX}', `${this._uploads().length + 1}.${mimeTypeData.FileExtension}`)),
                            new LogsContainer(logs));
                        this.validate(upload);
                        this._uploads.push(upload);
                        this._uploaderMode(QualzyUploaderModes.FilePicker);

                        this.autoUploadOnSingleFile();
                    }
                }
            };
        }

        if (this._config.AllowAudioRecording) {
            this.audioRecorderConfig = {
                StaticStrings: {
                    PermissionNotGranted: this._config.StaticStrings.PermissionNotGranted,
                    ReadyToRecord: this._config.StaticStrings.ReadyToRecord,
                    Recording: this._config.StaticStrings.Recording,
                    StartRecording: this._config.StaticStrings.StartRecording,
                    StopRecording: this._config.StaticStrings.StopRecording
                },
                Events: {
                    OnRecordingStopped: (data, mimeTypeData, logs) => {
                        //When the video recording is done, create a file of blob and add it to the list of upload operations
                        const upload = this.createUpload(
                            new File(data, this._config.StaticStrings.NewRecordedAudioName.replace('{SUFFIX}', `${this._uploads().length + 1}.${mimeTypeData.FileExtension}`)),
                            new LogsContainer(logs));
                        this.validate(upload);
                        this._uploads.push(upload);
                        this._uploaderMode(QualzyUploaderModes.FilePicker);

                        this.autoUploadOnSingleFile();
                    }
                }
            };
        }
        
        if (this._config.AllowScreenRecording) {
            this.screenRecorderConfig = {
                MicrophoneAllowed: this.microphoneAllowed,
                StaticStrings: {
                    PermissionNotGranted: this._config.StaticStrings.PermissionNotGranted,
                    StartRecording: this._config.StaticStrings.StartRecording,
                    AllowScreenRecording: this._config.StaticStrings.AllowScreenRecording,
                    StopRecording: this._config.StaticStrings.StopRecording
                },
                Events: {
                    OnRecordingStopped: (data, mimeTypeData, logs, hasVideoTrack) => {
                        //When the video recording is done, create a file of blob and add it to the list of upload operations
                        const upload = this.createUpload(
                            new File(data, this._config.StaticStrings.NewRecordedVideoName.replace('{SUFFIX}', `${this._uploads().length + 1}.${mimeTypeData.FileExtension}`)),
                            new LogsContainer(logs));
                        
                        upload.setNeedsVideoTrack(true);
                        upload.setHasVideoTrack(hasVideoTrack);
                        
                        this.validate(upload);
                        this._uploads.push(upload);
                        this._uploaderMode(QualzyUploaderModes.FilePicker);

                        this.autoUploadOnSingleFile();
                    }
                }
            };
        }

        if (this._config.AllowScreenAndVideoRecording) {
            this.screenAndVideoRecorderConfig = {
                MicrophoneAllowed: this.microphoneAllowed,
                StaticStrings: {
                    AllowScreenRecording: this._config.StaticStrings.AllowScreenRecording,
                    AllowVideoRecording: this._config.StaticStrings.AllowVideoRecording,
                    BottomLeft: this._config.StaticStrings.BottomLeft,
                    BottomMiddle: this._config.StaticStrings.BottomMiddle,
                    BottomRight: this._config.StaticStrings.BottomRight,
                    CenterLeft: this._config.StaticStrings.CenterLeft,
                    CenterMiddle: this._config.StaticStrings.CenterMiddle,
                    CenterRight: this._config.StaticStrings.CenterRight,
                    PipLarge: this._config.StaticStrings.PipLarge,
                    PipMedium: this._config.StaticStrings.PipMedium,
                    PipSmall: this._config.StaticStrings.PipSmall,
                    ScreenRecordingPermissionNotGranted: this._config.StaticStrings.ScreenRecordingPermissionNotGranted,
                    StartRecording: this._config.StaticStrings.StartRecording,
                    StopRecording: this._config.StaticStrings.StopRecording,
                    TopLeft: this._config.StaticStrings.TopLeft,
                    TopMiddle: this._config.StaticStrings.TopMiddle,
                    TopRight: this._config.StaticStrings.TopRight,
                    VideoRecordingPermissionNotGranted: this._config.StaticStrings.VideoRecordingPermissionNotGranted
                },
                Events: {
                    OnRecordingStopped: (data, mimeTypeData, logs, hasVideoTrack) => {
                        //When the video recording is done, create a file of blob and add it to the list of upload operations
                        const upload = this.createUpload(
                            new File(data, this._config.StaticStrings.NewRecordedVideoName.replace('{SUFFIX}', `${this._uploads().length + 1}.${mimeTypeData.FileExtension}`)),
                            new LogsContainer(logs));
                        
                        upload.setNeedsVideoTrack(true);
                        upload.setHasVideoTrack(hasVideoTrack);
                        
                        this.validate(upload);
                        this._uploads.push(upload);
                        this._uploaderMode(QualzyUploaderModes.FilePicker);

                        this.autoUploadOnSingleFile();
                    }
                }
            };
        }
    }

    dispose() {
        //Trigger it after half a second because sometimes it is triggered as soon as the window is closed and the upload is finalized.
        setTimeout(() => {
            if (this.uploads().length > 0) {
                const incompleteUploads = this.uploads().filter(u => ![FileUploadStatus.Uploaded, FileUploadStatus.Cancelled, FileUploadStatus.Failed].includes(u.status()));

                if (incompleteUploads.length > 0) {
                    incompleteUploads.forEach(u => u.cancelUpload());
                }
            }
        }, 500);
    }

    /**
     * Allow us to show the modes again
     */
    resetMode(resetUploads = false) {
        this._uploaderMode(QualzyUploaderModes.FilePicker);
        if (resetUploads) {
            this._uploads.removeAll();
        }
    }

    /**
     * Auto upload when only single file option
     */
    async autoUploadOnSingleFile() {
        if (!this._config.AllowMultipleFiles) {
            await this.upload();
        }
    }

    /**
     * Pick files from the file picker
     */
    async pickFiles(): Promise<File[]> {
        const pickerFiles = new Array<File>();

        if (this._config.AllowMultipleFiles) {
            //if we allow multiple files to be picked, add them one by one to the list
            const files = await FilesHelpers.pickFiles(this._config.AllowedFileExtensions);
            files.forEach(f => pickerFiles.push(f));
        } else {
            //if we allow just a single file to be picked at a time, add it to the list
            const file = await FilesHelpers.pickFile(this.allowCameraCapture, this._config.AllowedFileExtensions);
            pickerFiles.push(file);
        }

        //Go through each picked file, instantiate the right upload operation, validate it, and add it to the uploads list
        for (let f = 0; f < pickerFiles.length; f++) {
            const logs = new LogsContainer();
            const file = pickerFiles[f];

            logs.addEntry(`Picked File ${f + 1} - File Name: '${file.name}', Size: ${file.size} bytes`);
            
            logs.addEntry("Preparing and validating upload");
            const upload = this.createUpload(file, logs);
            const isValid = this.validate(upload);
            this._uploads.push(upload);
            
            if (isValid) {
                logs.addEntry("File is queued and is ready to upload");
            }
        }

        this._uploaderMode(QualzyUploaderModes.FilePicker);

        // if single file only, upload automatically after selection
        await this.autoUploadOnSingleFile();

        return pickerFiles;
    }

    /**
     * Invokes the component for video recording
     */
    @autobind
    recordVideo() {
        if (!this._config.AllowVideoRecording) return;
        this._uploaderMode(QualzyUploaderModes.VideoRecorder);
    }

    /**
     * Invokes the component for audio recording
     */
    @autobind
    recordAudio() {
        if (!this._config.AllowAudioRecording) return;
        this._uploaderMode(QualzyUploaderModes.AudioRecorder);
    }

    /**
     * Invokes the component for screen recording
     */
    @autobind
    recordScreen() {
        if (!this._config.AllowScreenRecording) return;
        this._uploaderMode(QualzyUploaderModes.ScreenRecorder);
    }

    /**
     * Invokes the component for screen and video recording
     */
    @autobind
    recordScreenAndVideo() {
        if (!this._config.AllowScreenAndVideoRecording) return;
        this._uploaderMode(QualzyUploaderModes.ScreenAndVideoRecorder);
    }
    
    /**
     * Perform the actual upload for all the files
     */
    public async upload(): Promise<void> {
        try {
            //Get all the upload tasks
            const uploadTasks = this._uploads()
                .filter(f => [FileUploadStatus.Initialized, FileUploadStatus.Cancelled].includes(f.status()))
                .map(f => f.upload());

            await Promise.all(uploadTasks); //wait until all tasks are done
        } catch (e) {
            //todo: we need to look into this later as to how we want to handle exceptions
            console.log(e);
        }
    }

    /**
     * Delete the upload operation from the list
     * @param upload The upload operation to delete
     */
    @autobind
    delete(upload: U) {
        this._uploads.remove(upload);
    }

    /**
     * Validates an upload operation
     * @param upload
     * @private
     */
    private validate(upload: U) {
        if (this._config.AllowedMaxSizeInBytes != null && upload.File.totalSize > this._config.AllowedMaxSizeInBytes) {
            upload.changeStatus(FileUploadStatus.ExceedsMaxSize);
            return false;
        } else if (this._config.AllowedFileExtensions.length > 0 && !this._config.AllowedFileExtensions.includes(upload.File.getFileExtension().toLowerCase())) {
            upload.changeStatus(FileUploadStatus.MismatchExtension);
            return false;
        }
        else if (upload.needsVideoTrack() && !upload.hasVideoTrack()) {
            upload.changeStatus(FileUploadStatus.NoVideoTrack);
            return false;
        }
        else {
            upload.changeTextStatus(this._config.StaticStrings.FileIsReadyToBeUploaded);
            return true;
        }
    }
}